// AboutDialog.js — Version info and changelog modal
import React from 'react';
import { VERSION, RELEASE_DATE, CHANGELOG } from '../version';

export default function AboutDialog({ isOpen, onClose }) {
  if (!isOpen) return null;

  const latestRelease = CHANGELOG[0];

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>StarLIMS Cloud Usage Reporting</h2>
            <div style={styles.versionBadge}>Version {VERSION}</div>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {/* Current Version Info */}
          <div style={styles.section}>
            <div style={styles.metaInfo}>
              <span style={styles.metaLabel}>Released:</span> {RELEASE_DATE}
            </div>
            <div style={styles.metaInfo}>
              <span style={styles.metaLabel}>Build:</span> React + FastAPI + PostgreSQL
            </div>
            <div style={styles.metaInfo}>
              <span style={styles.metaLabel}>Deployed:</span> AWS EC2 + RDS
            </div>
          </div>

          {/* Latest Changes */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>What's New in {latestRelease.version}</h3>
            <ul style={styles.highlights}>
              {latestRelease.highlights.map((item, idx) => (
                <li key={idx} style={styles.highlightItem}>{item}</li>
              ))}
            </ul>
          </div>

          {/* Changelog Sections */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Changelog</h3>

            {latestRelease.sections.added && (
              <div style={styles.changeSection}>
                <div style={styles.changeLabel}>✨ Added</div>
                <ul style={styles.changeList}>
                  {latestRelease.sections.added.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {latestRelease.sections.changed && (
              <div style={styles.changeSection}>
                <div style={styles.changeLabel}>🔄 Changed</div>
                <ul style={styles.changeList}>
                  {latestRelease.sections.changed.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {latestRelease.sections.fixed && (
              <div style={styles.changeSection}>
                <div style={styles.changeLabel}>🐛 Fixed</div>
                <ul style={styles.changeList}>
                  {latestRelease.sections.fixed.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {latestRelease.sections.security && (
              <div style={styles.changeSection}>
                <div style={styles.changeLabel}>🔒 Security</div>
                <ul style={styles.changeList}>
                  {latestRelease.sections.security.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Version History */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Version History</h3>
            <div style={styles.versionHistory}>
              {CHANGELOG.map((release, idx) => (
                <div key={idx} style={styles.versionItem}>
                  <span style={styles.versionNumber}>{release.version}</span>
                  <span style={styles.versionDate}>{release.date}</span>
                  <span style={styles.versionType}>
                    {release.type === 'major' ? '🎉 Major' :
                     release.type === 'minor' ? '✨ Minor' :
                     release.type === 'patch' ? '🔧 Patch' :
                     '🚀 Initial'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div style={styles.footer}>
            <p style={styles.footerText}>
              For support or issues, contact your system administrator.
            </p>
            <p style={styles.footerText}>
              © {new Date().getFullYear()} StarLIMS. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '90%',
    maxWidth: 700,
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '24px 24px 20px',
    borderBottom: '1px solid #e5e5e5',
  },
  title: {
    margin: '0 0 8px 0',
    fontSize: 20,
    fontWeight: 600,
    color: '#1a1a1a',
  },
  versionBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    backgroundColor: '#f0f9ff',
    color: '#0369a1',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: 32,
    color: '#999',
    cursor: 'pointer',
    lineHeight: 1,
    padding: 0,
    width: 32,
    height: 32,
  },
  content: {
    padding: 24,
    overflowY: 'auto',
    flex: 1,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    margin: '0 0 12px 0',
    color: '#1a1a1a',
  },
  metaInfo: {
    fontSize: 13,
    color: '#666',
    marginBottom: 6,
  },
  metaLabel: {
    fontWeight: 600,
    color: '#444',
  },
  highlights: {
    margin: 0,
    padding: '0 0 0 20px',
    listStyle: 'none',
  },
  highlightItem: {
    fontSize: 14,
    color: '#444',
    marginBottom: 8,
    paddingLeft: 8,
    position: 'relative',
    '::before': {
      content: '"→"',
      position: 'absolute',
      left: -12,
      color: '#0369a1',
    },
  },
  changeSection: {
    marginBottom: 16,
  },
  changeLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: '#666',
    marginBottom: 6,
  },
  changeList: {
    margin: 0,
    padding: '0 0 0 20px',
    fontSize: 13,
    color: '#555',
  },
  versionHistory: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  versionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 12px',
    backgroundColor: '#f9fafb',
    borderRadius: 6,
    fontSize: 13,
  },
  versionNumber: {
    fontWeight: 600,
    color: '#1a1a1a',
    minWidth: 60,
  },
  versionDate: {
    color: '#666',
    minWidth: 100,
  },
  versionType: {
    fontSize: 12,
    color: '#666',
  },
  footer: {
    marginTop: 24,
    paddingTop: 16,
    borderTop: '1px solid #e5e5e5',
    textAlign: 'center',
  },
  footerText: {
    margin: '4px 0',
    fontSize: 12,
    color: '#999',
  },
};
